import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
} from "react";
import { useSelector } from "react-redux";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import ReactGa from "react-ga4";

import request from "../utils/request";

const trackingId = process.env.REACT_APP_GA_TRACKING_ID;
if (trackingId && !ReactGa.isInitialized) {
  ReactGa.initialize(trackingId);
}

// Custom inputs for DatePicker
const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <input
    type="text"
    onClick={onClick}
    ref={ref}
    value={value}
    readOnly
    placeholder="Select date"
    className="
      w-full
      border-[1.5px] border-[#E5E7EB]
      rounded-[14px]
      px-4 py-[12px]
      bg-[rgba(249,250,251,0.8)]
      backdrop-blur-sm
      text-[14px] text-gray-800
      font-medium
      focus:outline-none
      focus:border-[#EF4949]
      focus:border-2
      focus:ring-2 focus:ring-[#EF4949]/20
      focus:bg-white
      cursor-pointer
      transition-all duration-200
      hover:border-[#D1D5DB]
      hover:bg-[rgba(255,255,255,0.9)]
    "
  />
));
CustomDateInput.displayName = "CustomDateInput";

const CustomTimeInput = forwardRef(({ value, onClick }, ref) => (
  <input
    type="text"
    onClick={onClick}
    ref={ref}
    value={value}
    readOnly
    placeholder="Select time"
    className="
      w-full
      border-[1.5px] border-[#E5E7EB]
      rounded-[14px]
      px-4 py-[12px]
      bg-[rgba(249,250,251,0.8)]
      backdrop-blur-sm
      text-[14px] text-gray-800
      font-medium
      focus:outline-none
      focus:border-[#EF4949]
      focus:border-2
      focus:ring-2 focus:ring-[#EF4949]/20
      focus:bg-white
      cursor-pointer
      transition-all duration-200
      hover:border-[#D1D5DB]
      hover:bg-[rgba(255,255,255,0.9)]
    "
  />
));
CustomTimeInput.displayName = "CustomTimeInput";

const SaveCartButton = ({ size = "md" }) => {
  const rawCartItems = useSelector((state) => state.cart.cartItems || {});
  const cartItems = Object.values(rawCartItems).flat();

  const [open, setOpen] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [autosavingKey, setAutosavingKey] = useState(0); // For animation trigger
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    arrivalDate: null,
    arrivalTime: null,
    consent: false,
  });

  // Read saved cart code once
  let initialCartCode = null;
  try {
    initialCartCode = localStorage.getItem("savedCartCode");
  } catch {
    initialCartCode = null;
  }

  const [savedCartCode, setSavedCartCode] = useState(initialCartCode);
  const hasSavedCode = Boolean(savedCartCode);

  const previousCartRef = useRef(null);

  // Debounced auto-update for existing saved cart
  const debouncedUpdate = useRef(
    debounce((cartCode, payload) => {
      setAutosaving(true);
      setAutosavingKey((prev) => prev + 1); // Trigger animation
      request
        .put(`/carts/${cartCode}`, payload)
        .then((res) => {
          if (res?.data?.success) {
            // Silent success
          }
        })
        .catch((err) => {
          console.error("Failed to auto-update cart:", err);
        })
        .finally(() => setAutosaving(false));
    }, 800)
  ).current;

  useEffect(() => {
    const propertyRaw = localStorage.getItem("property");
    if (!savedCartCode || !propertyRaw || !cartItems.length) return;

    let property = null;
    try {
      property = JSON.parse(propertyRaw);
    } catch {
      property = null;
    }
    if (!property?.uniqueId) return;

    if (!previousCartRef.current || !isEqual(previousCartRef.current, cartItems)) {
      previousCartRef.current = cartItems;

      debouncedUpdate(savedCartCode, {
        cartItems,
        propertyProps: property,
      });
    }
  }, [cartItems, savedCartCode, debouncedUpdate]);

  /* ----------------------- FORM HANDLERS ----------------------- */

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleConsent = (e) => {
    setForm((prev) => ({ ...prev, consent: e.target.checked }));
    setErrors((prev) => ({ ...prev, consent: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const emailRegex = /\S+@\S+\.\S+/;

    if (!form.firstName.trim()) nextErrors.firstName = "Required";
    if (!form.lastName.trim()) nextErrors.lastName = "Required";
    if (!form.email.trim() || !emailRegex.test(form.email.trim())) {
      nextErrors.email = "Valid email required";
    }
    if (!form.arrivalDate) nextErrors.arrivalDate = "Select your arrival date";
    if (!form.arrivalTime) nextErrors.arrivalTime = "Select your arrival time";
    if (!form.consent)
      nextErrors.consent = "Please agree so we can email your cart link";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    const propertyRaw = localStorage.getItem("property");
    let property = null;

    try {
      property = JSON.parse(propertyRaw);
    } catch {
      property = null;
    }

    if (!property?.uniqueId) {
      console.error("Missing property info. Please refresh and try again.");
      return;
    }

    if (!cartItems.length) {
      console.warn("Your cart is empty.");
      return;
    }

    if (!validate()) return;

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();

    const combined = new Date(
      form.arrivalDate.getFullYear(),
      form.arrivalDate.getMonth(),
      form.arrivalDate.getDate(),
      form.arrivalTime.getHours(),
      form.arrivalTime.getMinutes()
    );

    const localISOString = new Date(
      combined.getTime() - combined.getTimezoneOffset() * 60000
    ).toISOString();

    const payload = {
      firstName,
      lastName,
      email,
      arrivalDate: localISOString,
      arrivalTime: localISOString,
      consent: form.consent,
      cartItems,
      propertyProps: property,
      uniqueId: property.uniqueId,
    };

    try {
      setIsSubmitting(true);
      const res = await request.post("/carts", payload);

      if (res?.data?.success && res.data.cartCode) {
        localStorage.setItem("savedCartCode", res.data.cartCode);
        setSavedCartCode(res.data.cartCode);
        setOpen(false);
        setShowToast(true);

        ReactGa.event({
          category: "Cart",
          action: "Saved Cart Submitted",
          label: res.data.cartCode,
        });

        // Auto-hide toast
        setTimeout(() => setShowToast(false), 4000);
      } else {
        console.error("Error saving cart. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ----------------------- RENDER ----------------------- */

  // Hide button if no items
  if (!cartItems.length) return null;

  const basePill =
    size === "sm"
      ? {
          px: "10px",
          py: "4px",
          fontSize: "11px",
        }
      : {
          px: "14px",
          py: "6px",
          fontSize: "12px",
        };

  return (
    <>
      {/* Toast */}
      {showToast && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#111827",
            color: "white",
            borderRadius: "999px",
            boxShadow: "0 10px 30px rgba(15,23,42,0.22)",
            px: 3,
            py: 1.5,
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontSize: 13,
          }}
        >
          <span>We&apos;ve emailed you a link to your saved cart.</span>
          <Button
            onClick={() => setShowToast(false)}
            sx={{
              minWidth: 0,
              color: "white",
              fontSize: 16,
              padding: 0,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            ×
          </Button>
        </Box>
      )}

      {/* Trigger button */}
      <Button
        variant="outlined"
        onClick={() => !hasSavedCode && setOpen(true)}
        disabled={hasSavedCode}
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          fontFamily: "inherit",
          ...basePill,
          backgroundColor: "#EF4949",
          color: "#fff",
          borderColor: "transparent",
          cursor: hasSavedCode ? "not-allowed" : "pointer",
          boxShadow: "0 6px 18px rgba(248,113,113,0.35)",
          "&:hover": {
            backgroundColor: hasSavedCode ? "#EF4949" : "#cc0000",
            borderColor: "transparent",
          },
        }}
      >
        {autosaving ? (
          <Box
            key={autosavingKey}
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              color: "#ffffff !important",
              fontWeight: 600,
              animation: "fadeInPulse 0.5s ease-in-out",
              "@keyframes fadeInPulse": {
                "0%": { opacity: 0.6, transform: "scale(0.98)" },
                "50%": { opacity: 1, transform: "scale(1)" },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
            }}
          >
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Auto Saving
          </Box>
        ) : hasSavedCode ? (
          <Box
            component="span"
            sx={{
              color: "#ffffff !important",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            Saved Cart
          </Box>
        ) : (
          <>Save Cart for Later</>
        )}
      </Button>

      {/* Modal */}
      <Modal 
        open={open} 
        onClose={() => setOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            transition: 'opacity 0.3s ease-in-out',
          }
        }}
      >
        {/* Liquid glass modal container */}
        <div className="relative w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
            {/* Ambient glows for liquid glass effect */}
            <div className="absolute -top-8 -right-12 w-40 h-40 bg-[#EF494915] rounded-[40px] blur-3xl pointer-events-none animate-pulse" />
            <div className="absolute -bottom-10 -left-12 w-36 h-36 bg-[#6366F115] rounded-[36px] blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '1s' }} />
            
            <Box
              sx={{
                p: 0,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(32px)",
                WebkitBackdropFilter: "blur(32px)",
                borderRadius: "28px",
                width: "100%",
                position: "relative",
                border: "1px solid rgba(243, 244, 246, 0.9)",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.5) inset",
                overflow: "hidden",
                animation: "fadeInUp 0.4s ease-out",
                "@keyframes fadeInUp": {
                  from: {
                    opacity: 0,
                    transform: "translateY(20px) scale(0.96)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0) scale(1)",
                  },
                },
              }}
            >
              {/* Decorative top section */}
              <div className="relative bg-gradient-to-br from-[#FFF5F5] via-[#FFFFFF] to-[#F9FAFB] px-6 pt-6 pb-4 border-b border-[#E5E7EB]/50">
                {/* Close button - modern circular design */}
                <button
                  onClick={() => setOpen(false)}
                  className="
                    absolute top-5 right-5
                    h-8 w-8
                    flex items-center justify-center
                    rounded-full
                    bg-white/90
                    backdrop-blur-sm
                    border border-[#E5E7EB]
                    text-[16px] text-[#6B7280]
                    hover:text-[#EF4949]
                    hover:bg-white
                    hover:border-[#EF4949]/30
                    shadow-[0_4px_12px_rgba(15,23,42,0.08)]
                    transition-all duration-200
                    z-10
                    hover:scale-110
                    active:scale-95
                  "
                  aria-label="Close modal"
                >
                  ✕
                </button>

                {/* Icon/Visual element */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EF4949] to-[#DC2626] flex items-center justify-center shadow-lg shadow-[#EF4949]/20">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: 26,
                        fontWeight: 700,
                        marginBottom: 4,
                        color: "#111827",
                        lineHeight: "1.2",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Save Your Cart
                    </h3>
                  </div>
                </div>
                <p
                  style={{
                    fontSize: 15,
                    color: "#6B7280",
                    lineHeight: "1.6",
                    marginLeft: 52,
                  }}
                >
                  You can save your cart and return later to finish shopping. A link will be emailed to you, and it&apos;ll work on any device.
                </p>
              </div>

              {/* Form content */}
              <div className="px-6 py-5">

              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="First name"
                    name="firstName"
                    fullWidth
                    onChange={updateField}
                    error={!!errors.firstName}
                    helperText={errors.firstName || ""}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(249, 250, 251, 0.8)",
                        backdropFilter: "blur(8px)",
                        transition: "all 0.2s ease",
                        "& fieldset": {
                          borderColor: "#E5E7EB",
                          borderWidth: "1.5px",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "& fieldset": {
                            borderColor: "#D1D5DB",
                          },
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          "& fieldset": {
                            borderColor: "#EF4949",
                            borderWidth: "2px",
                          },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "14px",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: "#EF4949",
                        },
                      },
                    }}
                  />
                  <TextField
                    label="Last name"
                    name="lastName"
                    fullWidth
                    onChange={updateField}
                    error={!!errors.lastName}
                    helperText={errors.lastName || ""}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(249, 250, 251, 0.8)",
                        backdropFilter: "blur(8px)",
                        transition: "all 0.2s ease",
                        "& fieldset": {
                          borderColor: "#E5E7EB",
                          borderWidth: "1.5px",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "& fieldset": {
                            borderColor: "#D1D5DB",
                          },
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          "& fieldset": {
                            borderColor: "#EF4949",
                            borderWidth: "2px",
                          },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "14px",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: "#EF4949",
                        },
                      },
                    }}
                  />
                </div>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  onChange={updateField}
                  error={!!errors.email}
                  helperText={errors.email || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(249, 250, 251, 0.8)",
                      backdropFilter: "blur(8px)",
                      transition: "all 0.2s ease",
                      "& fieldset": {
                        borderColor: "#E5E7EB",
                        borderWidth: "1.5px",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        "& fieldset": {
                          borderColor: "#D1D5DB",
                        },
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "& fieldset": {
                          borderColor: "#EF4949",
                          borderWidth: "2px",
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "#EF4949",
                      },
                    },
                  }}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-[#111827] mb-2.5">
                      Arrival date
                    </label>
                    <DatePicker
                      selected={form.arrivalDate}
                      onChange={(date) =>
                        setForm((prev) => ({ ...prev, arrivalDate: date }))
                      }
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      onKeyDown={(e) => e.preventDefault()}
                      customInput={<CustomDateInput />}
                    />
                    {errors.arrivalDate && (
                      <p className="text-[12px] text-red-500 mt-1.5 font-medium">
                        {errors.arrivalDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-[#111827] mb-2.5">
                      Arrival time
                    </label>
                    <DatePicker
                      selected={form.arrivalTime}
                      onChange={(time) =>
                        setForm((prev) => ({ ...prev, arrivalTime: time }))
                      }
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      dateFormat="h:mm aa"
                      onKeyDown={(e) => e.preventDefault()}
                      popperPlacement="top-start"
                      popperClassName="z-[9999]"
                      customInput={<CustomTimeInput />}
                    />
                    {errors.arrivalTime && (
                      <p className="text-[12px] text-red-500 mt-1.5 font-medium">
                        {errors.arrivalTime}
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-[#FEF2F2] to-[#FFF5F5] border border-[#FEE2E2]">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.consent}
                          onChange={toggleConsent}
                          sx={{
                            color: "#EF4949",
                            padding: "4px",
                            "&.Mui-checked": {
                              color: "#EF4949",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: 20,
                            },
                          }}
                        />
                      }
                      label={
                        <span className="text-[14px] text-[#374151] font-medium leading-relaxed">
                          I agree to receive email reminders
                        </span>
                      }
                      sx={{ margin: 0 }}
                    />
                  </div>
                  {errors.consent && (
                    <p className="text-[12px] text-red-500 mt-2 ml-1 font-medium">
                      {errors.consent}
                    </p>
                  )}
                </div>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  sx={{
                    mt: 4,
                    py: 1.6,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: "-0.01em",
                    backgroundColor: isSubmitting ? "#9CA3AF" : "#EF4949",
                    background: isSubmitting 
                      ? "#9CA3AF" 
                      : "linear-gradient(135deg, #EF4949 0%, #DC2626 100%)",
                    boxShadow: isSubmitting 
                      ? "none" 
                      : "0 8px 24px rgba(239, 73, 73, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                      transition: "left 0.5s",
                    },
                    "&:hover": {
                      backgroundColor: isSubmitting ? "#9CA3AF" : "#DC2626",
                      background: isSubmitting 
                        ? "#9CA3AF" 
                        : "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
                      boxShadow: isSubmitting 
                        ? "none" 
                        : "0 12px 32px rgba(239, 73, 73, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.15) inset",
                      transform: isSubmitting ? "none" : "translateY(-2px)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                    "&:active": {
                      transform: isSubmitting ? "none" : "translateY(0)",
                      boxShadow: isSubmitting 
                        ? "none" 
                        : "0 6px 20px rgba(239, 73, 73, 0.3)",
                    },
                  }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving your cart...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Saved Cart Link
                    </span>
                  )}
                </Button>
              </div>
              </div>
            </Box>
          </div>
      </Modal>
    </>
  );
};

export default SaveCartButton;
